import { observable, action, computed, reaction } from 'mobx';
import i18next from 'i18next';
import Alert from 'react-s-alert';
import objectPath from 'object-path';

import { AppManager, Variables, Input } from 'core/index';
import { ProjectApp, ProjectModel } from 'apps/project/app';
import { headerStore } from 'apps/components/header/app';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { routerStore } from 'core/routing';
import { ProfileConnManager } from 'apps/profile/main/connManager';
// import { SuggestedProjectConnManager } from 'apps/project/connManager';
import { IssueTrackerAppManager } from 'apps/issueTracker/appManager';
import { ConnResultsModal } from 'apps/modals/connResultsModal';
import { InviteApp } from 'apps/group/app';
import { SpacesApp } from 'apps/space/app';
import { frameStore } from 'apps/components/Frame/appManager';

import {
  ProjectMutation,
  ProjectQuery,
  ContributorsQuery,
  UpdateProjectImportMutation,
  ProjectForkMutation,
  PollImportStatusQuery,
  CancelProjectImportMutation,
} from 'apps/project/gql';

import { FormStore } from 'libs/forms/app';
import { conf as forkConf } from 'apps/modals/fork/config.yaml';

export class ProjectAppManager extends AppManager {
  static registryName = 'ProjectAppManager';
  @observable retryToken = null;
  @observable retryingImport = false;
  @observable forkTargetSpace = null;
  @observable forkNewName = '';

  @observable projectQuery = null;

  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.AppRef('project', ProjectApp);
    this.AppRef('optionsOverride', SpacesApp);

    this.forkForm = new FormStore(this, forkConf, 'forkForm');

    this.issueTrackerAppManager = new IssueTrackerAppManager(this, {
      params: this.params,
    });
    this.contributorsConn = new ProfileConnManager(this, {
      initialBatchSize: 3,
      batchSize: 12,
      sortBy: 'recent',
      sortOptions: [],
      loadMoreQuery: ContributorsQuery,
    });
    this.variables = new Variables({
      space: () => this.params.get('profile') || this.params.get('initiative'),
      slug: () => this.params.get('slug'),
      contributorsCursor: () => null,
      contributorsCount: () => this.contributorsConn.variables.count,
      contributorsSortBy: () => this.contributorsConn.sortBy,
      includeFilesInfo: () => this.params.get('initiative') === '+opennext',
    });

    this.variables.onChange(['space', 'slug'], this.NewProjectQuery);

    this.projectInput = new Input({
      id: () => this.project.id,
      spaceId: () => this.project.inSpace.id,
      name: () => this.project.name,
      description: () => this.project.description,
      private: () => this.project.private,
      license: () => {
        if (this.project.license) {
          return this.project.license.name;
        }
        return '';
      },
      phaseId: () => {
        if (this.project.phase) {
          return this.project.phase.id;
        }
        return null;
      },
      tags: () =>
        this.project?.tags.map(tag => ({
          id: tag.id,
          name: tag.name,
          deleted: tag.deleted,
        })),
      image: () => {
        if (this.project.image) {
          return this.project.image.id;
        }
        return null;
      },
    });

    this.showingAuthModal = false;

    // when the project appmanager is loaded with a token available in sessionStorage
    this.retryToken = this.readTokenFromSessionStorage();

    // this.suggestedProjectsConnection = new SuggestedProjectConnManager(this, {
    //   batchSize: 3,
    //   loadMoreQuery: SuggestedProjectsQuery,
    //   projectId: () => this.project?.id,
    // });

    this.suggestions_loaded = false;

    this.disposers.push(
      reaction(
        () => ({
          workspaces: clientStore.workspaces,
          activeSpaceId: clientStore.activeSpaceId,
          optionsManager:
            this.forkForm.fieldStores.get('forkTargetSpace').optionsManager,
        }),
        ({ workspaces, activeSpaceId, optionsManager }) => {
          if (!optionsManager) return;
          this.optionsOverride = workspaces;
          // eslint-disable-next-line no-param-reassign
          optionsManager.options = this.optionsOverride;

          const activeSpace = optionsManager.options.find(
            option => option.id === activeSpaceId,
          );

          this.forkTargetSpace = activeSpace;
        },
      ),
    );
  }

  @computed get loading() {
    return this.projectQuery?.loading;
  }

  @computed get notFound() {
    return this.projectQuery && !this.projectQuery.loading && !this.project;
  }

  @computed get pendingInvitation() {
    const { pendingInvitations } = clientStore;
    const inSpaceID = objectPath.get(this, 'project.inSpace.id');
    if (!inSpaceID || !pendingInvitations) return null;
    const invite = pendingInvitations.find(
      ({ group }) => objectPath.get(group, 'space.id') === inSpaceID,
    );
    return invite ? new InviteApp(null, { id: invite.id }) : null;
  }

  @action NewProjectQuery = () => {
    if (!(this.variables.space && this.variables.slug)) return;

    // if (this.suggestedProjectsConnection) {
    //   this.suggestedProjectsConnection.Reset();
    //   this.suggestions_loaded = false; // reset the flag
    // }

    this.projectQuery = new ProjectQuery(this, {
      onSysErr: this.onError,
    });
    this.projectQuery.process(
      invites => invites,
      clientStore.SetPendingInvitations,
      {
        subPath: 'loggedInUser.pendingInvites',
      },
    );
    this.projectQuery.process(
      project => {
        if (project) {
          frameStore.SetProject(project);
        }
        return ProjectModel.Load(project);
      },
      this.ProjectUpdate,
      {
        subPath: 'project',
      },
    );
    this.projectQuery.process(
      this.contributorsConn.process,
      this.ContributorsUpdate,
      {
        subPath: 'project.contributors',
      },
    );
    this.projectQuery.query();
  };

  @action ProjectUpdate = projectModel => {
    this.project = projectModel;
    // frameStore.SetProject(this.project);

    if (this.project && this.retryToken && !this.retryingImport) {
      this.RetryImportProcess(this.retryToken);
    }
    if (this.project) {
      headerStore.SetPage(projectModel);
    }
    this.UpdateImportPolling(this.project);

    // if (
    //   this.project &&
    //   this.suggestedProjectsConnection &&
    //   !this.suggestions_loaded
    // ) {
    //   this.suggestions_loaded = true;
    //   this.suggestedProjectsConnection.loadMore();
    // }
  };

  @action ImportStatusUpdate = projectModel => {
    if (!this.project) return;
    this.project.importStatus = projectModel.importStatus;
  };

  @action UpdateImportPolling = project => {
    if (!project?.isImporting) {
      this.cleanImportStatusPollQuery();
      return;
    }
    if (this.importStatusPollQuery) return;
    this.importStatusPollQuery = new PollImportStatusQuery(this, {
      queryKey: 'projectImportStatus',
      pollInterval: 10000,
      fetchPolicy: 'no-cache',
    });
    this.importStatusPollQuery.process(
      result => ProjectModel.Load(result),
      projectModel => {
        if (!projectModel) return;
        if (
          projectModel.importStatus.status !==
          'importing_error_authorization_required'
        ) {
          this.retryToken = null;
        }
      },
      { ignoreInitial: true },
    );
    this.importStatusPollQuery.query();
  };

  @action ContributorsUpdate = (contributors, query) => {
    this.contributorsConn.pushResultIfFirst(contributors, query);
  };

  onSuccess = () => {
    const { project } = this;
    if (!project) return;
    project.SetEditing(false);
    project.SetSaving(false);
    project.ResetLocals();
    project.editFormStore.SetPosting(false);
    project.editFormStore.clearDraft();
    project.editLicenseFormStore = null;
    project.editFormStore = null;
    clientStore.UpdateProfileOrTeamWorkspace();
    Alert.success('Your Project has been updated successfully.');
  };

  onUserError = errs => {
    this.project?.SetSaving(false);
    this.project?.editFormStore.SetPosting(false);
    errs.forEach(err => {
      Alert.error(`${err.key}: ${err.message}`);
    });
  };

  onSysError = () => {
    this.project?.SetSaving(false);
    Alert.error('Hmm... We failed to update your Project. Please try again.');
    this.project?.editFormStore.SetPosting(false);
  };

  @action Consolidate = () => {
    const { project } = this;
    if (!project.editFormStore.isValid) {
      project.editFormStore.alert();
    } else {
      project.editFormStore.SetPosting(true);
      this.mutations.push(
        ProjectMutation.New(this.projectInput, {
          onSysErr: this.onSysError,
          onUserErr: this.onUserError,
          onSuccess: this.onSuccess,
        }),
      );
    }
  };

  Save = () => {
    const { project } = this;
    if (!project) return;
    project.SetSaving(true);
    const imageField = project.editFormStore.fieldStores.get('image');
    if (imageField?.fileModified) {
      imageField.getUpdate(this.Consolidate);
    } else {
      this.Consolidate();
    }
  };

  @action RetryImportProcess = newImportToken => {
    this.retryingImport = true;
    const { onRetrySysErr, onRetryUserErr, onRetrySuccess } = this;
    const options = { onRetrySysErr, onRetryUserErr, onRetrySuccess };

    // Call the Update Project Import mutation
    const updateProjectInput = new Input({
      projectId: () => this.project.id,
      importToken: () => newImportToken, // By default send an empty import token
    });

    this.mutations.push(
      UpdateProjectImportMutation.New(updateProjectInput, options),
    );
    this.UpdateImportPolling(this.project);
  };

  @action onRetrySysErr = () => {
    this.retryingImport = false;
    Alert.error(
      i18next.t(
        'Hmm... We found an error while retrying the process. Please try again later.',
      ),
    );
  };

  @action onRetryUserErr = error => {
    this.retryingImport = false;
    Alert.error(i18next.t(error));
  };

  @action onRetrySuccess = () => {
    this.retryingImport = false;
    Alert.success(i18next.t('We will retry the process'));
  };

  onForkError = () => {
    this.forkForm.SetPosting(false);
    Alert.error('Hmm... We failed to fork the Project. Please try again.');
  };

  onForkSuccess = forkedProject => {
    this.forkForm.SetPosting(false);
    Alert.success('The projects has been forked successfully.');
    // Create a temporary App to compute the route
    const forkedProjectApp = new ProjectApp(null, forkedProject);
    modalStore.ClearComponent();
    routerStore.navigate(
      forkedProjectApp.route.name,
      forkedProjectApp.route.params,
    );
  };

  Fork = () => {
    if (!this.forkForm.isValid) {
      this.forkForm.alert();
    }
    this.forkForm.SetPosting(true);
    const options = {
      onSysErr: this.onForkError,
      onUserErr: this.onForkError,
      onSuccess: this.onForkSuccess,
    };
    const input = new Input({
      projectId: this.project.id,
      targetSpaceSlug: this.forkTargetSpace.path,
      name: this.forkNewName,
    });
    const mutation = ProjectForkMutation.New(input, options);
    return mutation;
  };

  ShowMoreContributors = () => {
    modalStore.SetModalComponent(ConnResultsModal, {
      arrayApps: [this.contributorsConn.arrayApp],
      modalTitle: `"${this.project.name}" Contributors:`,
    });
  };

  readTokenFromSessionStorage() {
    const token = sessionStorage.getItem('temp-access-token');
    sessionStorage.removeItem('temp-access-token');
    return token;
  }

  ShowAuthModal = () => {
    if (!this.project?.isImporting) return;

    const { importService } = this.project.importStatus;

    switch (importService) {
      case 'google_drive':
        this.ShowAuthModalForGoogleDrive();
        break;

      case 'dropbox':
        this.ShowAuthModalForDropbox();
        break;

      default:
        Alert.error(
          i18next.t(`Error showing login modal for service ${importService}`),
        );
        break;
    }
  };

  ShowAuthModalForGoogleDrive = () => {
    const google_script_id = 'googleapi';

    // Show we load the script dinamically here, or at the start of the app?

    const performAuthFunction = () => {
      window.gapi.load('auth2', () => {
        window.gapi.auth2.authorize(
          {
            client_id: IMPORTER_GOOGLE_API_CLIENT_ID,
            scope: 'https://www.googleapis.com/auth/drive.readonly',
            immediate: false,
          },
          response => {
            if (response.error) {
              this.onAuthorizationError(response);
            } else if (response.access_token) {
              this.onAuthorizationSuccess(response.access_token);
            } else {
              this.onAuthorizationError(response);
            }
          },
        );
      });
    };

    if (document.getElementById(google_script_id) === null) {
      // If the script has not been added yet
      const script = document.createElement('script');
      script.id = google_script_id;
      script.src = 'https://apis.google.com/js/api.js';
      document.body.appendChild(script);
      script.onload = () => {
        // Now, load the libraries
        performAuthFunction();
      };
    } else {
      performAuthFunction();
    }
  };

  ShowAuthModalForDropbox = async () => {
    const redirectUri = new URL('redirect', MAIN_URL);
    const redirectTo = btoa(JSON.stringify(routerStore.route));
    const requestUrl = new URL('https://www.dropbox.com/oauth2/authorize');

    requestUrl.searchParams.set('client_id', IMPORTER_DROPBOX_APP_KEY);
    requestUrl.searchParams.set('redirect_uri', redirectUri.href);
    requestUrl.searchParams.set('force_reauthentication', true);
    requestUrl.searchParams.set('state', redirectTo);

    const pkce_codes = await this.generateCodeVerifierAndCodeChallenge();

    sessionStorage.setItem('pkce-code-verifier', pkce_codes.codeVerifier);
    requestUrl.searchParams.set('response_type', 'code');
    requestUrl.searchParams.set('code_challenge', pkce_codes.codeChallenge);
    requestUrl.searchParams.set(
      'code_challenge_method',
      pkce_codes.code_challenge_method,
    );

    window.location.href = requestUrl;
  };

  @action onAuthorizationSuccess = token => {
    this.RetryImportProcess(token);
  };

  @action onAuthorizationError = response => {
    let errorMessage = '';
    switch (response.error) {
      case 'popup_closed_by_user':
        errorMessage =
          'It looks like you closed the authorization window. Please, use the button to try again';
        break;

      default:
        errorMessage = JSON.stringify(response);
        break;
    }
    // TODO: If the user did not sign in, we should encourage her to try again using the button
    Alert.error(i18next.t(errorMessage));
    this.cleanImportStatusPollQuery();
  };

  @action cleanImportStatusPollQuery = () => {
    if (this.importStatusPollQuery) {
      this.importStatusPollQuery.disposer();
      this.importStatusPollQuery = null;
    }
  };

  @action CancelImportAction = () => {
    const cancelImportingProjectInput = new Input({
      projectId: () => this.project.id,
    });
    this.mutations.push(
      CancelProjectImportMutation.New(cancelImportingProjectInput, {
        onSysErr: this.onCancelImportSysErr,
        onUserErr: this.onCancelImportUserErr,
        onSuccess: this.onCancelImportSuccess,
      }),
    );
  };

  @action onCancelImportSuccess = () => {
    Alert.error(i18next.t('The importing process has been cancelled'));
    this.project.importStatus = null;
  };

  @action onCancelImportSysErr = response => {
    Alert.error(i18next.t(JSON.stringify(response)));
  };

  @action onCancelImportUserErr = response => {
    Alert.error(i18next.t(JSON.stringify(response)));
  };

  generateRandomBytes = length => {
    const randomBytes = new Uint8Array(length);
    window.crypto.getRandomValues(randomBytes);

    return randomBytes;
  };

  async generateCodeVerifierAndCodeChallenge() {
    // Using the method defined here: https://dropbox.tech/developers/pkce--what-and-why-
    const base64Encode = buffer => {
      const base64Url = btoa(
        String.fromCharCode.apply(null, new Uint8Array(buffer)),
      )
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

      return base64Url;
    };

    const generateCodeVerifier = () => {
      const randomBytes = this.generateRandomBytes(32);

      return base64Encode(randomBytes);
    };

    const encoder = new TextEncoder();
    const bufferData = encoder.encode(generateCodeVerifier());
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', bufferData);
    const codeChallenge = base64Encode(hashBuffer);

    const codeVerifier = generateCodeVerifier();

    return { codeVerifier, codeChallenge, code_challenge_method: 'S256' };
  }
}
